import * as v from 'valibot';

/**
 * A step that does not end.
 *
 * Every trigger reef had was a starting gun — a webhook fires, a run happens,
 * the run finishes. That covers "something happened, go and deal with it" and
 * misses the harder half: noticing that something has been slowly getting worse
 * for six days, which nothing fires an event for.
 *
 * A watcher is a step whose whole job is to hold a baseline and compare against
 * it. The baseline lives in D1 rather than in the agent's own state because the
 * Durable Object is evicted between firings — a watcher that lost its baseline
 * every time it went quiet would report every wake-up as a change.
 */
export const WatchStatus = v.picklist(['watching', 'tripped', 'stopped', 'failed']);
export type WatchStatus = v.InferOutput<typeof WatchStatus>;

export const WatchRecord = v.object({
  id: v.string(),
  workflowId: v.string(),
  stepId: v.string(),
  runId: v.string(),
  conversationId: v.string(),
  status: WatchStatus,
  /** What it is comparing against, as the watcher itself wrote it. */
  baseline: v.nullable(v.unknown()),
  cron: v.nullable(v.string()),
  nextCheckAt: v.nullable(v.number()),
  lastCheckedAt: v.nullable(v.number()),
  checks: v.pipe(v.number(), v.integer()),
  /** Times the condition held. Not the same as `checks`, and the difference is the point. */
  trips: v.pipe(v.number(), v.integer()),
  lastTripAt: v.nullable(v.string()),
  startedAt: v.string(),
  stoppedAt: v.nullable(v.string()),
  stoppedBy: v.nullable(v.string()),
});
export type WatchRecord = v.InferOutput<typeof WatchRecord>;

/**
 * What a step passes to `reef.watch()`.
 *
 * `until` is a plain description rather than an expression, because the
 * condition is evaluated by the watcher's own model against what it observes —
 * the thing being watched is usually not something a comparison operator can
 * reach. What reef enforces is the *cadence* and the ceiling, not the predicate.
 */
export const WatchWrite = v.object({
  /** Five-field cron, UTC. How often to look. */
  cron: v.string(),
  /** What the watcher is looking for, in its own words. */
  until: v.pipe(v.string(), v.minLength(4), v.maxLength(600)),
  /** Initial baseline, if the step already knows what "normal" looks like. */
  baseline: v.optional(v.unknown()),
  /** Stop watching after this many checks, whatever happens. */
  maxChecks: v.optional(v.pipe(v.number(), v.integer(), v.minValue(1)), 1000),
});
export type WatchWrite = v.InferOutput<typeof WatchWrite>;

/**
 * What the watcher reports back after a check.
 *
 * `tripped` is separate from `changed` because most changes are not the thing
 * you were watching for — a watcher that escalated on every difference would be
 * a pager that goes off when a graph moves.
 */
export const WatchObservation = v.object({
  tripped: v.boolean(),
  summary: v.pipe(v.string(), v.maxLength(600)),
  /** The new baseline, when this check should reset what "normal" means. */
  baseline: v.optional(v.unknown()),
});
export type WatchObservation = v.InferOutput<typeof WatchObservation>;
